import React, { useEffect } from "react";
import { Button, Drawer, Form, Input, Select, Space, Switch } from "antd";
import { ExpandOutlined, CompressOutlined, TeamOutlined } from "@ant-design/icons";
import {
  BUSINESS_USER_ROLES,
  BUSINESS_USER_STATUSES,
  formatEnumLabel,
} from "../constants";

export default function OrganizationUserDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
}) {
  const [form] = Form.useForm();
  const [fullWidth, setFullWidth] = React.useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [open, form, initialValues]);

  const handleFinish = async (values) => {
    try {
      await onSubmit({
        ...values,
        primaryContact: Boolean(values.primaryContact),
      });
    } catch {
      // Caller is responsible for showing toast / errors.
      // Prevent unhandled promise rejections from triggering the runtime overlay.
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <TeamOutlined />
          <span>{initialValues?.id ? "Edit Organization User" : "Add Organization User"}</span>
        </Space>
      }
      width={fullWidth ? "100vw" : 520}
      styles={{
        body: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        header: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        footer: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
      }}
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      zIndex={2000}
      extra={
        <Space>
          <Button
            type="text"
            aria-label={fullWidth ? "Normal drawer width" : "Expand drawer"}
            title={fullWidth ? "Normal width" : "Expand"}
            icon={fullWidth ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setFullWidth((v) => !v)}
          />
        </Space>
      }
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ status: "ACTIVE", primaryContact: false }}
      >
        <Form.Item
          name="email"
          label="User Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Invalid email" },
          ]}
        >
          <Input placeholder="user@domain.com" disabled={Boolean(initialValues?.id)} />
        </Form.Item>

        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Role is required" }]}
        >
          <Select
            options={BUSINESS_USER_ROLES.map((v) => ({
              value: v,
              label: formatEnumLabel(v),
            }))}
          />
        </Form.Item>

        <Form.Item name="status" label="Status">
          <Select
            options={BUSINESS_USER_STATUSES.map((v) => ({
              value: v,
              label: formatEnumLabel(v),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="primaryContact"
          label="Primary Contact"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
